import { type FC, memo, useEffect } from 'react';
import InputSection from './input-section';
import plusIcon from '@assets/images/vault/plus.svg';
import styles from './index.module.scss';

import { numericFormat } from '@utils/index';
import { type CoinStruct } from '@models/coins.vault.model';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import { scaleDown } from '@utils/math';

import { type Pool } from '@cetusprotocol/cetus-sui-clmm-sdk';
import { BN, Decimal } from 'parrot-sui-sdk';
import { useEstLiquidityAndCoinAmountFromOneAmounts } from '@hooks/use-liquidity-amounts';
import { useComputed } from 'foca';
import { globalSettingModel } from '@models/global-setting.model';

interface OwnProps {
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  pool: Pool | undefined;
  coinA: CoinStruct;
  coinB: CoinStruct;
  tabValue: number;
  amountA: string;
  amountB: string;
  onChangeAmountA(amountA: string): void;
  onChangeAmountB(amountB: string): void;
  exactIn: boolean;
  onChangeExactIn(value: boolean): void;
}
const DepositAmount: FC<OwnProps> = (props) => {
  const {
    vault,
    pool,
    coinA,
    coinB,
    tabValue,
    onChangeAmountA,
    onChangeAmountB,
    amountA,
    amountB,
    exactIn,
    onChangeExactIn,
  } = props;
  const slippage = useComputed(globalSettingModel.liquiditySlippage);

  const amounts = useEstLiquidityAndCoinAmountFromOneAmounts(
    vault.tick_lower,
    vault.tick_upper,
    new BN(exactIn ? amountA : amountB),
    exactIn,
    true,
    new Decimal(slippage).div(100).toNumber(),
    pool ? new BN(pool.current_sqrt_price) : undefined,
  );

  useEffect(() => {
    if (tabValue === 1) {
      if (amounts) {
        if (exactIn) {
          onChangeAmountB(amounts.amount_b.toString());
        } else {
          onChangeAmountA(amounts.amount_a.toString());
        }
      } else {
        if (exactIn) {
          onChangeAmountB('');
        } else {
          onChangeAmountA('');
        }
      }
    }
  }, [exactIn, amounts?.amount_b, amounts?.amount_a, tabValue]);

  const handleAmountAChanged = (value: string) => {
    onChangeExactIn(true);
    onChangeAmountA(value);
  };

  const handleAmountBChanged = (value: string) => {
    onChangeExactIn(false);
    onChangeAmountB(value);
  };

  const _total = scaleDown(amountA === '' ? 0 : amountA, coinA.decimals)
    .mul(coinA?.price_usd || 0)
    .add(scaleDown(amountB === '' ? 0 : amountB, coinB.decimals).mul(coinB?.price_usd || 0));

  return (
    <div className={`${styles.wrapper}`}>
      <div className={styles.main}>
        {tabValue !== 3 && (
          <InputSection coin={coinA} amount={amountA} onAmountChange={handleAmountAChanged} />
        )}
        {tabValue === 1 && (
          <>
            <div className={styles.plus}>
              <img src={plusIcon} />
            </div>
            <div className={styles.space}></div>
          </>
        )}

        {tabValue !== 2 && (
          <InputSection coin={coinB} amount={amountB} onAmountChange={handleAmountBChanged} />
        )}
      </div>
      <div className={styles.total}>
        <div>Total</div>
        <div>${numericFormat(_total.toString())}</div>
      </div>
    </div>
  );
};

export default memo(DepositAmount);
